import {savePayStructure} from '../services/payStructureStorage';
import {validDate} from '../features/expenses/expenseModel';
import usePayStructureHistory from '../hooks/usePayStructureHistory';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { syncData, db } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { normalizePayStructure } from '../features/payperiod/payStructure';

// Create context with default values
const DataContext = createContext({
  logs: [],
  updateLogs: () => {},
  loading: true,
  syncing: false,
  isNewUser: false,
  paymentConfig: null,
  needsOnboarding: false,
  completeOnboarding: () => Promise.resolve(),
  forceSync: () => Promise.resolve(false),
  payPeriodAnchor: null,
  payPeriodNeedsConfirmation: false,
  updatePayPeriodAnchor: () => Promise.resolve(),
  periodRecords: {},
  updatePeriodRecord: () => Promise.resolve()
});

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]); // Initialize with empty array
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [lastSaveStatus, setLastSaveStatus] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const payHistory = usePayStructureHistory(user);
  const [paymentConfig, setPaymentConfig] = useState(normalizePayStructure(null));
  const [hasSavedPayStructure,setHasSavedPayStructure] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [payPeriodAnchor, setPayPeriodAnchor] = useState(null);
  const [payPeriodNeedsConfirmation, setPayPeriodNeedsConfirmation] = useState(false);
  const [periodRecords, setPeriodRecords] = useState({});

  // Initial data load - with error handling and retry mechanism
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    
    const loadData = async () => {
      setLoading(true);
      setLoadError('');
      setLogs([]);
      setHasSavedPayStructure(false);
      setPaymentConfig(normalizePayStructure(null));
      setLastSaveStatus('');
      try {
        console.log("Loading user data...");
        
        // Handle guest users - load from localStorage
        if (user.isGuest) {
          try {
            const guestLogs = localStorage.getItem(`guestLogs_${user.uid}`);
            const guestConfig = localStorage.getItem(`guestConfig_${user.uid}`);
            const guestAnchor = localStorage.getItem(`payPeriodAnchor_${user.uid}`);
            const guestPeriods = localStorage.getItem(`periodRecords_${user.uid}`);
            
            // Real guest ledgers start empty. Tour examples are UI-only.
            const demoLogs = guestLogs ? JSON.parse(guestLogs) : [];
            setLogs(demoLogs);
            setPaymentConfig(normalizePayStructure(guestConfig ? JSON.parse(guestConfig) : null));
            setHasSavedPayStructure(!!guestConfig);
            setPayPeriodAnchor(guestAnchor || null);
            setPayPeriodNeedsConfirmation(!guestAnchor && demoLogs.length > 0);
            setPeriodRecords(guestPeriods ? JSON.parse(guestPeriods) : {});
            setIsNewUser(demoLogs.length === 0);
            // First-run pay setup for guests too, survives reload via localStorage.
            setNeedsOnboarding(!localStorage.getItem(`onboarded_${user.uid}`) && !guestConfig);
          } catch (err) {
            console.error("Error loading guest data:", err);
            setLogs([]);
            setLoadError("Your saved work could not be loaded. Please retry.");
          }
          setLoading(false);
          return;
        }
        
        // Try to process pending transactions, but continue even if it fails
        try {
          await syncData.processPendingTransactions(user.uid);
        } catch (syncErr) {
          console.warn("Non-critical error processing transactions:", syncErr);
        }
        
        // Fetch all data with retries
        let data = null;
        let retries = 0;
        const maxRetries = 3;
        
        // Try to get logs directly from the sync service
        try {
          // Get delivery logs
          const userDocRef = doc(db, 'users', user.uid, 'deliveryLogs', 'data');
          const userDoc = await getDoc(userDocRef);
          
          let logsEmpty = true;
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userLogs = userData.logs || [];
            console.log("Successfully loaded logs:", userLogs.length);
            setLogs(userLogs);
            setIsNewUser(userLogs.length === 0);
            logsEmpty = userLogs.length === 0;
          } else {
            setLogs([]);
            setIsNewUser(true);
          }

          // Get payment config from main user document
          const mainUserDocRef = doc(db, 'users', user.uid);
          const mainUserDoc = await getDoc(mainUserDocRef);
          const mainData = mainUserDoc.exists() ? mainUserDoc.data() : {};
          if (mainData.paymentConfig) {
            setPaymentConfig(normalizePayStructure(mainData.paymentConfig));
            setHasSavedPayStructure(true);
          }
          setPayPeriodAnchor(mainData.payPeriodAnchor || null);
          setPayPeriodNeedsConfirmation(!mainData.payPeriodAnchor && !logsEmpty);
          setPeriodRecords(mainData.periodRecords || {});

          // First-run: prompt pay setup only for a genuinely new user who has no
          // logs, no saved pay config, and hasn't already completed onboarding.
          setNeedsOnboarding(logsEmpty && !mainData.onboarded && !mainData.paymentConfig);
          
        } catch (directErr) {
          console.warn("Direct fetch failed, trying force refresh:", directErr);
          
          // Fallback to force refresh
          while (!data && retries < maxRetries) {
            try {
              data = await syncData.forceRefreshAllData(user.uid);
              if (!data || data.success === false) throw new Error("Refresh failed");
              if (data.logs) setLogs(data.logs);
              if (data.paymentConfig) {setPaymentConfig(normalizePayStructure(data.paymentConfig));setHasSavedPayStructure(true);}
              break;
            } catch (fetchErr) {
              console.warn(`Fetch attempt ${retries + 1} failed:`, fetchErr);
              data = null;
              retries++;
              if (retries < maxRetries) {
                await new Promise(r => setTimeout(r, 1000 * retries));
              }
            }
          }
          
          // If still no data, set empty
          if (!data || data.success === false) {
            setLoadError("Your saved work could not be loaded. Please retry.");
            console.warn("Could not fetch fresh data, using fallback");
            setLogs([]);
          }
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setLoadError("Your saved work could not be loaded. Please retry.");
        // Set empty logs as fallback
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  // Update logs and sync to backend
  const updateLogs = async (newLogs) => {
    if (!user?.uid) throw new Error('Sign in before saving.');
    let result;
    if (user.isGuest) {
      localStorage.setItem(`guestLogs_${user.uid}`, JSON.stringify(newLogs));
      result = { success: true, isOnline: false };
      setLastSaveStatus('guest');
    } else {
      result = await syncData.saveDeliveryLogs(user.uid, newLogs);
      if (!result?.success) throw new Error('Could not save your work.');
      setLastSaveStatus(result.isOnline ? 'synced' : 'local');
    }
    setLogs(newLogs);
    setIsNewUser(false);
    return result;
  };

  const adoptPaymentConfig = config => {
    if (user?.isGuest) localStorage.setItem(`guestConfig_${user.uid}`, JSON.stringify(config));
    setPaymentConfig(normalizePayStructure(config));
    setHasSavedPayStructure(true);
  };

  // Complete first-run onboarding: optionally save the chosen pay config, mark
  // the account onboarded, and clear the gate. Guests persist locally.
  const completeOnboarding = async (config, options = {}) => {
    if (!validDate(options.payPeriodAnchor)) throw new Error('Choose a valid pay-period start.');
    const saved = await savePayStructure({user,config,previous:hasSavedPayStructure?paymentConfig:null,onboardingOptions:options});
    setPaymentConfig(saved);setHasSavedPayStructure(true);
    setPayPeriodAnchor(options.payPeriodAnchor);setPayPeriodNeedsConfirmation(false);
    return saved;
  };

  const updatePayPeriodAnchor = async date => {
    if (!validDate(date)||!user?.uid) throw new Error('Choose a valid period date.');
    if (!user.isGuest&&!navigator.onLine) throw new Error('Connect before saving.');
    if (user.isGuest) localStorage.setItem(`payPeriodAnchor_${user.uid}`,date);
    else await setDoc(doc(db,'users',user.uid),{payPeriodAnchor:date,updatedAt:new Date().toISOString()},{merge:true});
    setPayPeriodAnchor(date);setPayPeriodNeedsConfirmation(false);
  };

  const updatePeriodRecord = async (periodId, patch, strict = false) => {
    try {
      if (!periodId || !user?.uid) throw new Error('Sign in before saving a check.');
      if (!user.isGuest && !navigator.onLine) throw new Error('Connect before saving a check.');
      const record = { ...(periodRecords[periodId] || {}), ...patch, updatedAt: new Date().toISOString() };
      if (user.isGuest) {
        const key = `periodRecords_${user.uid}`;
        localStorage.setItem(key, JSON.stringify({ ...JSON.parse(localStorage.getItem(key) || '{}'), [periodId]: record }));
      } else await setDoc(doc(db, 'users', user.uid), { periodRecords: { [periodId]: record }, updatedAt: new Date().toISOString() }, { merge: true });
      setPeriodRecords(previous => ({ ...previous, [periodId]: record }));
      return true;
    } catch (err) { if (strict) throw err; return false; }
  };

  // Force sync all data
  const forceSync = async () => {
    if (!user?.uid) return;
    
    setSyncing(true);
    try {
      if (user.isGuest) {
        // For guest users, just reload from localStorage
        const guestLogs = localStorage.getItem(`guestLogs_${user.uid}`);
        const guestConfig = localStorage.getItem(`guestConfig_${user.uid}`);
        
        if (guestLogs) setLogs(JSON.parse(guestLogs));
        if (guestConfig) setPaymentConfig(normalizePayStructure(JSON.parse(guestConfig)));
        setLoadError('');
        return true;
      } else {
        // For regular users, sync with Firebase
        await syncData.processPendingTransactions(user.uid);
        const refreshedData = await syncData.forceRefreshAllData(user.uid);
        if (!refreshedData || refreshedData.success === false) throw new Error("Could not refresh");
        
        if (refreshedData && refreshedData.logs) {
          setLogs(refreshedData.logs);
        }
        
        if (refreshedData && refreshedData.paymentConfig) {
          setPaymentConfig(normalizePayStructure(refreshedData.paymentConfig));
        }
        
        setLoadError('');
        setLastSaveStatus('synced');
        return true;
      }
    } catch (err) {
      console.error("Error syncing data:", err);
      return false;
    } finally {
      setSyncing(false);
    }
  };

  const value = {
    logs,
    loadError,
    lastSaveStatus,
    adoptPaymentConfig,
    updateLogs,
    loading,
    syncing,
    isNewUser,
    paymentConfig,
    hasSavedPayStructure,
    payStructureVersions: payHistory.versions,
    payHistoryLoading: payHistory.loading,
    payHistoryError: payHistory.error,
    needsOnboarding,
    completeOnboarding,
    finishOnboarding: () => setNeedsOnboarding(false),
    forceSync,
    payPeriodAnchor,
    payPeriodNeedsConfirmation,
    updatePayPeriodAnchor,
    periodRecords,
    adoptPeriodRecords: patches => setPeriodRecords(previous => ({ ...previous, ...patches })),
    updatePeriodRecord
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataContext;